import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchMyOrders } from "../services/orderService";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const STATUS_STYLE = {
  Confirmed: { bg: "rgba(45,106,79,.1)", color: "#2d6a4f", dot: "#2d6a4f" },
  Packed:    { bg: "rgba(212,160,23,.1)", color: "#b8860b", dot: "#d4a017" },
  Shipped:   { bg: "rgba(74,144,226,.1)", color: "#1a6eb5", dot: "#4a90e2" },
  Delivered: { bg: "rgba(39,103,73,.12)", color: "#276749", dot: "#40916c" },
  Cancelled: { bg: "rgba(192,57,43,.1)", color: "#c0392b", dot: "#c0392b" },
  Pending:   { bg: "rgba(90,114,98,.1)", color: "#5a7262", dot: "#5a7262" },
};

/* ─────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Pending;
  return (
    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
      <span className="status-badge__dot" style={{ background: s.dot }} />
      {status}
    </span>
  );
}

/* ─────────────────────────────────────────────
   ORDER CARD
───────────────────────────────────────────── */
function OrderCard({ order }) {
  const firstItem = order.orderItems?.[0];
  const extraCount = (order.orderItems?.length || 1) - 1;

  return (
    <Link to={`/order-confirmation/${order.id || order._id}`} className="order-card">
      <div className="order-card__top">
        <div className="order-card__meta">
          <span className="order-card__num">#{order.orderNumber}</span>
          <span className="order-card__date">{formatDate(order.createdAt)}</span>
        </div>
        <StatusBadge status={order.orderStatus} />
      </div>

      <div className="order-card__body">
        <div className="order-card__img">
          {firstItem?.image
            ? <img src={firstItem.image} alt={firstItem.name} />
            : <span>🌿</span>}
        </div>
        <div className="order-card__info">
          <p className="order-card__name">{firstItem?.name || "Product"}</p>
          {extraCount > 0 && (
            <p className="order-card__more">+{extraCount} more item{extraCount > 1 ? "s" : ""}</p>
          )}
          <p className="order-card__qty">Qty: {order.orderItems?.reduce((s, i) => s + i.quantity, 0) || 1}</p>
        </div>
        <div className="order-card__total">{fmt(order.total)}</div>
      </div>

      <div className="order-card__footer">
        <span className="order-card__payment">
          {order.paymentStatus === "paid"
            ? <><span style={{ color: "#2d6a4f" }}>✓</span> Paid</>
            : order.paymentStatus}
        </span>
        <span className="order-card__view">View Details →</span>
      </div>
    </Link>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();

  const FILTERS = ["All", "Confirmed", "Packed", "Shipped", "Delivered", "Cancelled"];

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) { navigate("/login"); return; }

    (async () => {
      try {
        const result = await fetchMyOrders();
        if (result?.success) {
          const list = result.data?.orders || result.data || [];
          setOrders(Array.isArray(list) ? list : []);
        } else {
          setError(result?.message || "Failed to load orders.");
        }
      } catch {
        setError("Something went wrong.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const filtered = filter === "All" ? orders : orders.filter(o => o.orderStatus === filter);

  return (
    <>
      <style>{css}</style>
      <div className="orders-page">
        <div className="orders-container">

          {/* Header */}
          <div className="orders-header">
            <Link to="/profile" className="orders-back">←</Link>
            <div>
              <h1 className="orders-title">My Orders</h1>
              <p className="orders-sub">{orders.length} order{orders.length !== 1 ? "s" : ""} placed</p>
            </div>
          </div>

          {/* Filter tabs */}
          {!loading && !error && orders.length > 0 && (
            <div className="orders-filters">
              {FILTERS.map(f => (
                <button
                  key={f}
                  className={`filter-tab ${filter === f ? "filter-tab--active" : ""}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                  {f !== "All" && orders.filter(o => o.orderStatus === f).length > 0 && (
                    <span className="filter-tab__count">{orders.filter(o => o.orderStatus === f).length}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="orders-state">
              <div className="orders-spinner" />
              <p>Loading your orders…</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="orders-state">
              <span style={{ fontSize: "2.5rem" }}>🌿</span>
              <p style={{ color: "#c0392b" }}>{error}</p>
              <Link to="/products" className="state-btn">Continue Shopping</Link>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && orders.length === 0 && (
            <div className="orders-state">
              <span style={{ fontSize: "3rem" }}>🛒</span>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", color: "#1b4332" }}>No orders yet</h3>
              <p style={{ color: "#5a7262" }}>Your order history will appear here once you place your first order.</p>
              <Link to="/products" className="state-btn">Start Shopping</Link>
            </div>
          )}

          {/* Empty filter */}
          {!loading && !error && orders.length > 0 && filtered.length === 0 && (
            <div className="orders-state">
              <span style={{ fontSize: "2rem" }}>🔍</span>
              <p style={{ color: "#5a7262" }}>No {filter.toLowerCase()} orders found.</p>
              <button className="state-btn" onClick={() => setFilter("All")}>Show all orders</button>
            </div>
          )}

          {/* Order list */}
          {!loading && !error && filtered.length > 0 && (
            <div className="orders-list">
              {filtered.map(order => (
                <OrderCard key={order._id || order.id} order={order} />
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary: #2d6a4f;
  --c-primary-dark: #1b4332;
  --c-primary-light: #40916c;
  --c-bg: #f9f6f0;
  --c-surface: #ffffff;
  --c-text: #1c2b22;
  --c-muted: #5a7262;
  --c-border: #d4e6d5;
  --f-display: 'Cormorant Garamond', Georgia, serif;
  --f-body: 'Jost', sans-serif;
  --radius: 14px;
  --radius-sm: 8px;
  --ease: cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }
a { text-decoration: none; color: inherit; }

.orders-page { min-height: 100vh; background: var(--c-bg); padding-bottom: 60px; }
.orders-container { max-width: 680px; margin: 0 auto; padding: 0 20px; }

/* ── HEADER ── */
.orders-header {
margin-top:50px;
  display: flex; align-items: center; gap: 16px;
  padding: 24px 0 20px;
}
.orders-back {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--c-surface); border: 1px solid var(--c-border);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; color: var(--c-primary); flex-shrink: 0;
  transition: background .2s;
}
.orders-back:hover { background: var(--c-border); }
.orders-title { font-family: var(--f-display); font-size: 1.8rem; font-weight: 600; color: var(--c-primary-dark); }
.orders-sub { font-size: .78rem; color: var(--c-muted); margin-top: 2px; }

/* ── FILTERS ── */
.orders-filters {
  display: flex; gap: 8px; flex-wrap: wrap;
  margin-bottom: 20px; padding-bottom: 4px;
}
.filter-tab {
  font-family: var(--f-body); font-size: .76rem; font-weight: 600;
  letter-spacing: .04em; padding: 6px 14px; border-radius: 100px;
  border: 1.5px solid var(--c-border); background: var(--c-surface);
  color: var(--c-muted); cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  transition: all .22s var(--ease);
}
.filter-tab:hover { border-color: var(--c-primary-light); color: var(--c-primary); }
.filter-tab--active {
  background: var(--c-primary); border-color: var(--c-primary); color: #fff;
}
.filter-tab__count {
  background: rgba(255,255,255,.25); color: inherit;
  font-size: .65rem; font-weight: 700;
  padding: 1px 6px; border-radius: 100px;
}
.filter-tab--active .filter-tab__count { background: rgba(255,255,255,.3); }

/* ── STATES ── */
.orders-state {
  min-height: 40vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 14px;
  text-align: center; color: var(--c-muted);
}
.orders-spinner {
  width: 36px; height: 36px; border-radius: 50%;
  border: 3px solid var(--c-border); border-top-color: var(--c-primary);
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.state-btn {
  font-family: var(--f-body); font-size: .82rem; font-weight: 700;
  letter-spacing: .06em; text-transform: uppercase;
  padding: 11px 24px; border-radius: 100px; border: none;
  background: var(--c-primary); color: #fff; cursor: pointer;
  transition: background .22s; text-decoration: none; display: inline-block;
}
.state-btn:hover { background: var(--c-primary-dark); }

/* ── ORDER LIST ── */
.orders-list { display: flex; flex-direction: column; gap: 14px; }

/* ── ORDER CARD ── */
.order-card {
  background: var(--c-surface); border: 1px solid var(--c-border);
  border-radius: var(--radius); overflow: hidden;
  display: block; transition: border-color .22s, box-shadow .22s, transform .22s;
  animation: cardIn .4s var(--ease) both;
  text-decoration: none; color: inherit;
}
.order-card:hover {
  border-color: var(--c-primary-light);
  box-shadow: 0 6px 24px rgba(45,106,79,.12);
  transform: translateY(-2px);
}
@keyframes cardIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }

.order-card__top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px 12px; border-bottom: 1px solid var(--c-border);
  background: var(--c-bg);
}
.order-card__meta { display: flex; align-items: center; gap: 10px; }
.order-card__num { font-size: .82rem; font-weight: 700; color: var(--c-text); font-family: monospace; }
.order-card__date { font-size: .74rem; color: var(--c-muted); }

.status-badge {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: .7rem; font-weight: 700; letter-spacing: .04em;
  padding: 4px 10px; border-radius: 100px;
}
.status-badge__dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

.order-card__body { display: flex; align-items: center; gap: 14px; padding: 16px 18px; }
.order-card__img {
  width: 52px; height: 52px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #f0f7f2, #e8f5ec);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; overflow: hidden;
}
.order-card__img img { width: 100%; height: 100%; object-fit: cover; }
.order-card__info { flex: 1; min-width: 0; }
.order-card__name { font-size: .9rem; font-weight: 600; color: var(--c-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 3px; }
.order-card__more { font-size: .74rem; color: var(--c-primary); font-weight: 500; margin-bottom: 2px; }
.order-card__qty { font-size: .74rem; color: var(--c-muted); }
.order-card__total {
  font-size: 1.15rem;
  font-weight: 600; color: var(--c-primary-dark); white-space: nowrap;
}

.order-card__footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 18px; border-top: 1px solid var(--c-border);
  background: var(--c-bg);
}
.order-card__payment { font-size: .74rem; color: var(--c-muted); display: flex; align-items: center; gap: 4px; }
.order-card__view { font-size: .74rem; font-weight: 600; color: var(--c-primary); letter-spacing: .04em; }

@media (max-width: 480px) {
  .orders-container { padding: 0 14px; }
  .orders-filters { gap: 6px; }
  .filter-tab { font-size: .7rem; padding: 5px 11px; }
  .order-card__body { gap: 10px; padding: 14px; }
  .order-card__total { font-size: 1rem; }
}
`;
